import {
  concat,
  createPublicClient,
  createWalletClient,
  custom,
  defineChain,
  encodeAbiParameters,
  encodePacked,
  http,
  parseUnits,
  type Address,
  type Hex,
} from 'viem'
import './styles.css'

declare global {
  interface Window {
    ethereum?: unknown
  }
}

const CHAIN_ID = 4663
const RPC = 'https://rpc.mainnet.chain.robinhood.com'
const ROUTER: Address = '0x8876789976dEcBfCbBbe364623C63652db8C0904'
const POOL: Address = '0x3Ad2640Aa2a81d82117c8142BA82d45FfE8308b3'
const CB: Address = '0xab46accEb52C3aBc3E891C17734CD0d09d9D38c9'
const WETH: Address = '0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73'
const ADDRESS_THIS: Address = '0x0000000000000000000000000000000000000002'
const CMD_WRAP_ETH: Hex = '0x0b'
const CMD_V3_SWAP_EXACT_IN: Hex = '0x00'
const Q192 = 2n ** 192n
const SLIPPAGE = 5n

const robinhood = defineChain({
  id: CHAIN_ID,
  name: 'Robinhood Chain',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: { default: { http: [RPC] } },
  blockExplorers: {
    default: { name: 'Blockscout', url: 'https://robinhoodchain.blockscout.com' },
  },
})

const POOL_ABI = [
  {
    type: 'function', name: 'slot0', stateMutability: 'view', inputs: [], outputs: [
      { name: 'sqrtPriceX96', type: 'uint160' }, { name: 'tick', type: 'int24' },
      { name: 'observationIndex', type: 'uint16' }, { name: 'observationCardinality', type: 'uint16' },
      { name: 'observationCardinalityNext', type: 'uint16' }, { name: 'feeProtocol', type: 'uint8' },
      { name: 'unlocked', type: 'bool' },
    ],
  },
  { type: 'function', name: 'token0', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'fee', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint24' }] },
] as const

const ROUTER_ABI = [{
  type: 'function',
  name: 'execute',
  stateMutability: 'payable',
  inputs: [
    { name: 'commands', type: 'bytes' },
    { name: 'inputs', type: 'bytes[]' },
    { name: 'deadline', type: 'uint256' },
  ],
  outputs: [],
}] as const

const publicClient = createPublicClient({ chain: robinhood, transport: http(RPC) })

const app = must<HTMLElement>('app')
const game = must<HTMLElement>('game')
const capyWrap = must<HTMLElement>('capyWrap')
const capyBody = must<HTMLElement>('capyBody')
const buyBtn = must<HTMLButtonElement>('buyBtn')
const buyLine = must<HTMLElement>('buyLine')
const statusEl = must<HTMLElement>('status')
const amounts = must<HTMLElement>('amounts')
const customRow = must<HTMLElement>('customRow')
const customAmount = must<HTMLInputElement>('customAmount')
const winCard = must<HTMLElement>('winCard')
const winWallet = must<HTMLElement>('winWallet')

let amountUsd = 5
let address: Address | null = null
let pressure = 0
let busy = false
let lastRound = 0
let explodedRound = 0
const demo = new URLSearchParams(location.search).get('demo') === '1'

type GameState = {
  roundId: number
  prizeUsd: number
  pressure: number
  ended: boolean
  winner: Address | null
  winningTx: Hex | null
}

function must<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id)
  if (!el) throw new Error(`Missing #${id}`)
  return el as T
}

function shortAddress(value: string): string {
  return `${value.slice(0, 6)}…${value.slice(-4)}`
}

function setStatus(text: string, kind: 'normal' | 'error' | 'ok' = 'normal') {
  statusEl.textContent = text
  statusEl.className = `tiny${kind === 'normal' ? '' : ` ${kind}`}`
}

function setPressure(next: number) {
  pressure = Math.max(0, Math.min(.965, next))
  const x = 1 + pressure * .38
  const y = 1 + pressure * .105
  capyBody.style.setProperty('--sx', x.toFixed(4))
  capyBody.style.setProperty('--sy', y.toFixed(4))
  app.dataset.pressure = pressure > .86 ? '3' : pressure > .68 ? '2' : pressure > .48 ? '1' : '0'
}

function pumpAnimation(power = 1) {
  game.classList.remove('pumping')
  capyBody.classList.remove('puff')
  void game.offsetWidth
  game.classList.add('pumping')
  capyBody.classList.add('puff')
  const duration = Math.min(920, 620 + power * 20)
  window.setTimeout(() => {
    game.classList.remove('pumping')
    capyBody.classList.remove('puff')
  }, duration)
}

function explode(state: GameState) {
  if (explodedRound === state.roundId) return
  explodedRound = state.roundId
  capyWrap.classList.add('explode')
  game.classList.add('booming')
  setPressure(.965)

  window.setTimeout(() => {
    const mine = address && state.winner && address.toLowerCase() === state.winner.toLowerCase()
    winWallet.textContent = mine
      ? 'YOU POPPED THE CAPY'
      : state.winner
        ? `${shortAddress(state.winner)} popped it`
        : 'Winner confirmed'
    winCard.hidden = false
  }, 720)

  window.setTimeout(() => {
    winCard.hidden = true
    capyWrap.classList.remove('explode')
    game.classList.remove('booming')
  }, 3900)
}

function updateBuyLabel() {
  const shown = Number.isInteger(amountUsd) ? String(amountUsd) : amountUsd.toFixed(2)
  buyLine.textContent = `BUY $${shown} CB`
}

amounts.addEventListener('click', (event) => {
  const target = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-value]')
  if (!target) return
  amounts.querySelectorAll('button').forEach((button) => button.classList.remove('active'))
  target.classList.add('active')

  if (target.dataset.value === 'other') {
    customRow.hidden = false
    customAmount.focus()
    if (Number(customAmount.value) > 0) amountUsd = Number(customAmount.value)
  } else {
    customRow.hidden = true
    amountUsd = Number(target.dataset.value)
  }
  updateBuyLabel()
})

customAmount.addEventListener('input', () => {
  const value = Number(customAmount.value)
  if (Number.isFinite(value) && value > 0) {
    amountUsd = value
    updateBuyLabel()
  }
})

async function ensureWallet(): Promise<Address> {
  if (!window.ethereum) throw new Error('Open this page inside a wallet browser')
  const provider = window.ethereum as { request(args: { method: string; params?: unknown[] }): Promise<unknown> }
  const accounts = await provider.request({ method: 'eth_requestAccounts' }) as string[]
  if (!accounts[0]) throw new Error('No wallet account selected')

  const currentHex = await provider.request({ method: 'eth_chainId' }) as string
  if (Number.parseInt(currentHex, 16) !== CHAIN_ID) {
    try {
      await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: `0x${CHAIN_ID.toString(16)}` }] })
    } catch {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: `0x${CHAIN_ID.toString(16)}`,
          chainName: 'Robinhood Chain',
          nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
          rpcUrls: [RPC],
          blockExplorerUrls: ['https://robinhoodchain.blockscout.com'],
        }],
      })
    }
  }

  address = accounts[0] as Address
  return address
}

async function ethPriceUsd(): Promise<number> {
  const response = await fetch('/api/price', { cache: 'no-store' })
  if (!response.ok) throw new Error('ETH price is temporarily unavailable')
  const data = await response.json() as { priceUsd: number }
  if (!Number.isFinite(data.priceUsd) || data.priceUsd <= 0) throw new Error('Bad ETH price')
  return data.priceUsd
}

function quoteCb(amountIn: bigint, sqrtPriceX96: bigint, token0: Address, fee: number): bigint {
  const inIsToken0 = token0.toLowerCase() === WETH.toLowerCase()
  const gross = inIsToken0
    ? (amountIn * sqrtPriceX96 * sqrtPriceX96) / Q192
    : (amountIn * Q192) / (sqrtPriceX96 * sqrtPriceX96)
  return (gross * (1_000_000n - BigInt(fee))) / 1_000_000n
}

function swapInput(recipient: Address, amountIn: bigint, minOut: bigint, path: Hex): Hex {
  return encodeAbiParameters(
    [
      { type: 'address' }, { type: 'uint256' }, { type: 'uint256' },
      { type: 'bytes' }, { type: 'bool' }, { type: 'bytes' },
    ],
    [recipient, amountIn, minOut, path, false, '0x'],
  )
}

async function buyCb(usd: number): Promise<Hex> {
  const account = await ensureWallet()
  const price = await ethPriceUsd()
  const ethText = (usd / price).toFixed(18).replace(/0+$/, '').replace(/\.$/, '')
  const amountIn = parseUnits(ethText || '0', 18)
  if (amountIn <= 0n) throw new Error('Amount is too small')

  const [slot0, token0, fee] = await Promise.all([
    publicClient.readContract({ address: POOL, abi: POOL_ABI, functionName: 'slot0' }),
    publicClient.readContract({ address: POOL, abi: POOL_ABI, functionName: 'token0' }),
    publicClient.readContract({ address: POOL, abi: POOL_ABI, functionName: 'fee' }),
  ])

  const sqrtPriceX96 = slot0[0]
  const estimated = quoteCb(amountIn, sqrtPriceX96, token0, Number(fee))
  const minOut = estimated * (100n - SLIPPAGE) / 100n
  const path = encodePacked(['address', 'uint24', 'address'], [WETH, Number(fee), CB])
  const wrap = encodeAbiParameters([{ type: 'address' }, { type: 'uint256' }], [ADDRESS_THIS, amountIn])
  const commands = concat([CMD_WRAP_ETH, CMD_V3_SWAP_EXACT_IN]) as Hex
  const inputs = [wrap, swapInput(account, amountIn, minOut, path)] as const
  const deadline = BigInt(Math.floor(Date.now() / 1000) + 600)

  const provider = window.ethereum as Parameters<typeof custom>[0]
  const walletClient = createWalletClient({ account, chain: robinhood, transport: custom(provider) })

  const { request } = await publicClient.simulateContract({
    account,
    address: ROUTER,
    abi: ROUTER_ABI,
    functionName: 'execute',
    args: [commands, inputs, deadline],
    value: amountIn,
  })
  return walletClient.writeContract(request)
}

async function claimPump(hash: Hex): Promise<{ addedUsd: number; state: GameState }> {
  const response = await fetch('/api/pump', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ txHash: hash, wallet: address }),
  })
  const data = await response.json() as { error?: string; addedUsd?: number; state?: GameState }
  if (!response.ok || !data.state) throw new Error(data.error || 'Could not register the buy')
  return { addedUsd: data.addedUsd ?? amountUsd, state: data.state }
}

async function getState(): Promise<GameState> {
  const response = await fetch('/api/state', { cache: 'no-store' })
  if (!response.ok) throw new Error('Game server unavailable')
  return response.json() as Promise<GameState>
}

async function syncState() {
  if (demo || busy) return
  try {
    const state = await getState()
    if (lastRound && state.roundId !== lastRound) {
      capyWrap.classList.remove('explode')
      game.classList.remove('booming')
      winCard.hidden = true
    }
    lastRound = state.roundId
    setPressure(state.pressure)
    if (state.ended) explode(state)
  } catch {
    // Keep the last visual state. The buy button will show actionable errors.
  }
}

buyBtn.addEventListener('click', async () => {
  if (busy || !Number.isFinite(amountUsd) || amountUsd <= 0) return

  if (demo) {
    pumpAnimation(amountUsd)
    const next = Math.min(.98, pressure + Math.max(.018, Math.log10(amountUsd + 1) * .028))
    setPressure(next)
    if (next >= .95) {
      explode({ roundId: ++lastRound, prizeUsd: 100, pressure: 1, ended: true, winner: null, winningTx: null })
      window.setTimeout(() => setPressure(0), 4200)
    }
    return
  }

  busy = true
  buyBtn.disabled = true
  try {
    setStatus(address ? 'Confirm the buy in your wallet' : 'Connect wallet to pump')
    const hash = await buyCb(amountUsd)
    setStatus('Buying CB…')
    await publicClient.waitForTransactionReceipt({ hash })
    setStatus('Pumping…', 'ok')
    const result = await claimPump(hash)
    pumpAnimation(result.addedUsd)
    setPressure(result.state.pressure)
    lastRound = result.state.roundId
    if (result.state.ended) explode(result.state)
    else window.setTimeout(() => setStatus('The buy that pops the Capy wins $100'), 1100)
  } catch (error) {
    const message = error instanceof Error ? error.message.split('\n')[0] : 'Buy failed'
    setStatus(message, 'error')
  } finally {
    busy = false
    buyBtn.disabled = false
  }
})

setPressure(0)
updateBuyLabel()
if (demo) {
  setStatus('DEMO MODE · every click pumps the Capy', 'ok')
} else {
  void syncState()
  window.setInterval(() => void syncState(), 1400)
}
